import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DatabaseZap, Loader2, ShoppingBag, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@quebon/ui/components/button'
import { Card, CardAction, CardDescription, CardHeader, CardTitle } from '@quebon/ui/components/card'
import { adminApi } from '@/api/admin'
import type { AdminOrganization } from '@/api/admin'
import { LcOperationalResetDialog } from '@/components/organizations/LcOperationalResetDialog'
import { LcOperationModeField } from '@/components/organizations/LcOperationModeField'
import { TestCompletionRunHistory } from '@/components/organizations/TestCompletionRunHistory'

interface Props {
  organization: AdminOrganization
  onResetRequested: () => void
}

export function LcOrganizationSettingsTab({ organization, onResetRequested }: Props) {
  const queryClient = useQueryClient()
  const [isEditingTrial, setIsEditingTrial] = useState(false)
  const [trialEnabled, setTrialEnabled] = useState(organization.lcTrialEnabled)
  const [isEditingMall, setIsEditingMall] = useState(false)
  const [mallEnabled, setMallEnabled] = useState(organization.lcMallEnabled)
  const [isOperationalResetOpen, setIsOperationalResetOpen] = useState(false)

  const trialProgram = useQuery({
    queryKey: ['admin', 'organizations', organization.id, 'lc-free-trial-program'],
    queryFn: () => adminApi.getOrganizationLcTrialProgram(organization.id),
  })

  const updateTrial = useMutation({
    mutationFn: () => adminApi.updateOrganizationSettings(organization.id, { lcTrialEnabled: trialEnabled }),
    onSuccess: () => {
      queryClient.setQueryData<AdminOrganization>(
        ['admin', 'organizations', organization.id],
        (current) => current ? { ...current, lcTrialEnabled: trialEnabled } : current,
      )
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations', organization.id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] })
      queryClient.invalidateQueries({
        queryKey: ['admin', 'organizations', organization.id, 'lc-free-trial-program'],
      })
      setIsEditingTrial(false)
      toast.success('무료체험 설정이 수정되었습니다.')
    },
    onError: () => {
      toast.error('무료체험 설정 변경에 실패했습니다.')
    },
  })

  const cancelTrialEdit = () => {
    setTrialEnabled(organization.lcTrialEnabled)
    setIsEditingTrial(false)
  }

  const updateMall = useMutation({
    mutationFn: () => adminApi.updateOrganizationSettings(organization.id, { lcMallEnabled: mallEnabled }),
    onSuccess: () => {
      queryClient.setQueryData<AdminOrganization>(
        ['admin', 'organizations', organization.id],
        (current) => current ? { ...current, lcMallEnabled: mallEnabled } : current,
      )
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] })
      setIsEditingMall(false)
      toast.success('깨봉몰 운영 설정이 수정되었습니다.')
    },
    onError: () => {
      toast.error('깨봉몰 운영 설정 변경에 실패했습니다.')
    },
  })

  const cancelMallEdit = () => {
    setMallEnabled(organization.lcMallEnabled)
    setIsEditingMall(false)
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <LcOperationModeField
          organizationId={organization.id}
          mode={organization.lcOperationMode}
          onChanged={(mode) => {
            queryClient.setQueryData<AdminOrganization>(
              ['admin', 'organizations', organization.id],
              (current) => current ? { ...current, lcOperationMode: mode } : current,
            )
            queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] })
          }}
          onResetRequested={onResetRequested}
        />
        {organization.lcOperationMode === 'TEST' ? (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onResetRequested}
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
            >
              센터 학습 데이터 초기화
            </button>
          </div>
        ) : null}
        <TestCompletionRunHistory organizationId={organization.id} />
      </div>

      <section className="rounded-xl border border-sky-200 bg-sky-50/60 p-5" aria-label="깨봉몰 운영">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-sky-100 p-2 text-sky-700">
              <ShoppingBag className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-sky-950">깨봉몰 운영</h3>
              <p className="mt-1 text-xs text-sky-800">
                끄면 해당 센터 LMS에서 깨봉몰 메뉴와 화면 진입을 차단합니다.
              </p>
            </div>
          </div>
          {isEditingMall ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={cancelMallEdit}
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => updateMall.mutate()}
                disabled={updateMall.isPending}
                className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-800 disabled:opacity-50"
              >
                {updateMall.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                저장
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setMallEnabled(organization.lcMallEnabled)
                setIsEditingMall(true)
              }}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              수정
            </button>
          )}
        </div>

        {isEditingMall ? (
          <label className="mt-5 flex cursor-pointer items-center justify-between rounded-lg border border-sky-200 bg-white px-4 py-3">
            <span>
              <span className="block text-sm font-semibold text-slate-900">센터 LMS에서 깨봉몰 운영</span>
              <span className="mt-0.5 block text-xs text-slate-500">
                API는 그대로 사용할 수 있으며 메뉴 노출과 직접 URL 진입만 제어합니다.
              </span>
            </span>
            <input
              type="checkbox"
              checked={mallEnabled}
              onChange={(event) => setMallEnabled(event.target.checked)}
              className="h-5 w-5 rounded border-sky-300 text-sky-600 focus:ring-sky-500"
            />
          </label>
        ) : (
          <div className="mt-5 flex items-center gap-2">
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
              organization.lcMallEnabled
                ? 'bg-sky-100 text-sky-800'
                : 'bg-slate-200 text-slate-700'
            }`}>
              {organization.lcMallEnabled ? '운영 중' : '운영 중지'}
            </span>
            <span className="text-xs text-slate-500">
              {organization.lcMallEnabled ? '깨봉몰 메뉴와 화면을 노출합니다.' : '깨봉몰 메뉴와 화면을 숨깁니다.'}
            </span>
          </div>
        )}
      </section>

      <section className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-5" aria-label="무료체험 운영">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-emerald-100 p-2 text-emerald-700">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-emerald-950">무료체험 운영</h3>
              <p className="mt-1 text-xs text-emerald-800">
                승인된 센터만 신규 원생에게 무료체험을 적용할 수 있습니다.
              </p>
            </div>
          </div>
          {isEditingTrial ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={cancelTrialEdit}
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => updateTrial.mutate()}
                disabled={updateTrial.isPending}
                className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800 disabled:opacity-50"
              >
                {updateTrial.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                저장
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setTrialEnabled(organization.lcTrialEnabled)
                setIsEditingTrial(true)
              }}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              수정
            </button>
          )}
        </div>

        {isEditingTrial ? (
          <label className="mt-5 flex cursor-pointer items-center justify-between rounded-lg border border-emerald-200 bg-white px-4 py-3">
            <span>
              <span className="block text-sm font-semibold text-slate-900">무료체험 신규 부여 허용</span>
              <span className="mt-0.5 block text-xs text-slate-500">
                OFF로 변경해도 이미 대기·진행 중인 무료체험은 유지됩니다.
              </span>
            </span>
            <input
              type="checkbox"
              checked={trialEnabled}
              onChange={(event) => setTrialEnabled(event.target.checked)}
              className="h-5 w-5 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
            />
          </label>
        ) : (
          <div className="mt-5 space-y-4">
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                !organization.lcTrialEnabled
                  ? 'bg-slate-200 text-slate-700'
                  : trialProgram.data?.open
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-amber-100 text-amber-800'
              }`}>
                {!organization.lcTrialEnabled
                  ? '신규 부여 중지'
                  : trialProgram.isLoading
                    ? '현황 확인 중'
                    : trialProgram.data?.open
                      ? '운영 중'
                      : '운영 종료'}
              </span>
              <span className="text-xs text-slate-500">최초 적용일부터 90일 · 최대 80명</span>
            </div>

            <dl className="grid gap-3 sm:grid-cols-4">
              <div className="rounded-lg border border-emerald-100 bg-white p-3">
                <dt className="text-xs text-slate-500">최초 적용일</dt>
                <dd className="mt-1 text-sm font-semibold text-slate-900">{trialProgram.data?.firstGrantedOn ?? '-'}</dd>
              </div>
              <div className="rounded-lg border border-emerald-100 bg-white p-3">
                <dt className="text-xs text-slate-500">운영 종료일</dt>
                <dd className="mt-1 text-sm font-semibold text-slate-900">{trialProgram.data?.closesOn ?? '-'}</dd>
              </div>
              <div className="rounded-lg border border-emerald-100 bg-white p-3">
                <dt className="text-xs text-slate-500">사용 인원</dt>
                <dd className="mt-1 text-sm font-semibold text-slate-900">
                  {trialProgram.data ? `${trialProgram.data.grantedCount} / 80명` : '-'}
                </dd>
              </div>
              <div className="rounded-lg border border-emerald-100 bg-white p-3">
                <dt className="text-xs text-slate-500">남은 인원</dt>
                <dd className="mt-1 text-sm font-semibold text-slate-900">
                  {trialProgram.data ? `${trialProgram.data.remainingGrants}명` : '-'}
                </dd>
              </div>
            </dl>
          </div>
        )}
      </section>

      <Card>
        <CardHeader>
          <CardTitle>센터 운영 데이터 초기화</CardTitle>
          <CardDescription>
            상품·계약·LC 설정과 원장 계정은 유지하고, 센터의 원생·클래스·결제·교재 요청·문의·기기 데이터를 삭제합니다.
          </CardDescription>
          <CardAction>
            <Button type="button" variant="destructive" onClick={() => setIsOperationalResetOpen(true)}>
              <DatabaseZap data-icon="inline-start" />
              초기화
            </Button>
          </CardAction>
        </CardHeader>
      </Card>

      <LcOperationalResetDialog
        open={isOperationalResetOpen}
        organizationId={organization.id}
        centerName={organization.name}
        onOpenChange={setIsOperationalResetOpen}
      />
    </div>
  )
}
